import { supabase } from '@/integrations/supabase/client';

const IMPERSONATION_ORIGIN_KEY = 'impersonation_origin_session';
const IMPERSONATION_STATE_KEY = 'impersonation_state';

export interface ImpersonationTarget {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  role: string | null;
}

interface ImpersonationState {
  target: ImpersonationTarget;
  startedAt: string;
}

/**
 * Desembrulha a mensagem de erro de uma edge function (o corpo JSON vem em
 * error.context como Response), no mesmo padrão de representanteUsuariosService.
 */
async function invocarImpersonation(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke(
    'superadmin-impersonar-usuario',
    { body },
  );

  if (error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      const errorBody = await context.clone().json().catch(() => null);
      if (errorBody?.error) throw new Error(errorBody.error);
    }
    throw error;
  }
  if (data?.error) throw new Error(data.error);
  return data as {
    email: string;
    tokenHash: string;
    target: ImpersonationTarget;
  };
}

export const impersonationService = {
  estaImpersonando(): boolean {
    return localStorage.getItem(IMPERSONATION_STATE_KEY) !== null;
  },

  getTarget(): ImpersonationTarget | null {
    const raw = localStorage.getItem(IMPERSONATION_STATE_KEY);
    if (!raw) return null;
    try {
      return (JSON.parse(raw) as ImpersonationState).target;
    } catch {
      return null;
    }
  },

  /**
   * Inicia a personificação: guarda a sessão do admin, gera uma sessão real do
   * usuário alvo (via edge function + verifyOtp) e recarrega o app como o alvo.
   */
  async iniciar(targetUserId: string): Promise<void> {
    // 1) Salva a sessão atual (admin) para poder voltar
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Nenhuma sessão ativa para personificar a partir dela.');
    }
    localStorage.setItem(IMPERSONATION_ORIGIN_KEY, JSON.stringify(session));

    try {
      // 2) Gera o token de sessão do alvo
      const { tokenHash, target } = await invocarImpersonation({
        action: 'start',
        targetUserId,
      });

      // 3) Estabelece a sessão do alvo no client.
      // Com token_hash, o GoTrue exige APENAS token_hash + type (não enviar email).
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'magiclink',
      });
      if (verifyError) throw verifyError;

      // 4) Marca o estado de personificação para o banner
      const state: ImpersonationState = { target, startedAt: new Date().toISOString() };
      localStorage.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(state));

      // 5) Reload total → AuthProvider inicializa já como o alvo
      window.location.assign('/');
    } catch (error) {
      // Falhou antes de trocar a sessão: descarta o backup para não vazar estado
      localStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
      throw error;
    }
  },

  /**
   * Encerra a personificação: restaura a sessão do admin e volta ao /superadmin.
   */
  async encerrar(): Promise<void> {
    const raw = localStorage.getItem(IMPERSONATION_ORIGIN_KEY);
    if (!raw) {
      // Sem backup: apenas limpa o estado e desloga por segurança
      localStorage.removeItem(IMPERSONATION_STATE_KEY);
      await supabase.auth.signOut();
      window.location.assign('/autenticacao');
      return;
    }

    const origin = JSON.parse(raw) as { access_token: string; refresh_token: string };
    // setSession renova o access token via refresh token caso já tenha expirado
    const { error } = await supabase.auth.setSession({
      access_token: origin.access_token,
      refresh_token: origin.refresh_token,
    });

    localStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
    localStorage.removeItem(IMPERSONATION_STATE_KEY);

    if (error) {
      // Não foi possível restaurar: desloga para forçar novo login do admin
      await supabase.auth.signOut();
      window.location.assign('/autenticacao');
      return;
    }

    window.location.assign('/superadmin');
  },
};
